import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'

import FileCopySharpIcon from '@mui/icons-material/FileCopySharp'
import FolderSharpIcon from '@mui/icons-material/FolderSharp'
import InfoSharpIcon from '@mui/icons-material/InfoSharp'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {Box, Divider, Grid, Theme, Tooltip} from '@mui/material'
import {PGButton, PGTypography} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {useResponsive} from '@progyny/hooks/useResponsive'
import {ExistingClaimDocument} from '@progyny/store/claims'

interface RemoteFilePreviewProps {
  document: ExistingClaimDocument
  hideDivider?: boolean
  removeDocument?: (document: ExistingClaimDocument) => void
}

export const RemoteFilePreview: FC<RemoteFilePreviewProps> = (props) => {
  const {document, hideDivider, removeDocument} = props
  const {classes} = useStyles()
  const {t} = useTranslation()
  const isDownSM = useResponsive('down', 'sm')
  const removeFileName = t('Remove {{fileName}}', {fileName: document.fileName})

  const navigateToPreview = () => {
    if (document.fileUrl) {
      window.open(document.fileUrl, '_blank')
    }
  }

  return (
    <>
      <Grid
        container
        spacing={3}
        mb={2}
        pb={2}
        alignItems={'center'}
        className={classes.root}
      >
        {!isDownSM && (
          <Grid item xs={4}>
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'flex-start'}
              alignItems={'center'}
            >
              <Box mr={1} mt={0.5}>
                <FolderSharpIcon className={classes.validIcon} />
              </Box>
              <Box>
                <PGTypography variant={'caption'}>
                  {t(document.documentType)}
                </PGTypography>
              </Box>
            </Box>
          </Grid>
        )}
        <Grid item xs={4}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            sx={{cursor: 'pointer', minWidth: 0}}
            onClick={navigateToPreview}
          >
            <Box mr={1} mt={0.5} sx={{flexShrink: 0}}>
              <FileCopySharpIcon className={classes.fileIcon} />
            </Box>
            <Box sx={{minWidth: 0, flex: 1}}>
              <Tooltip title={document.fileName}>
                <span>
                  <PGTypography
                    variant={'caption'}
                    className={classes.fileName}
                    data-testid={'file-name'}
                  >
                    {document.fileName}
                  </PGTypography>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4} sm={4}>
          {document.description && (
            <Box
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'flex-start'}
              sx={{minWidth: 0}}
            >
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                mr={1}
                sx={{flexShrink: 0}}
              >
                <InfoSharpIcon className={classes.validIcon} />
              </Box>
              <Tooltip title={document.description}>
                <span>
                  <PGTypography
                    variant={'caption'}
                    className={classes.description}
                  >
                    {document.description}
                  </PGTypography>
                </span>
              </Tooltip>
            </Box>
          )}
        </Grid>
      </Grid>
      {removeDocument && (
        <Grid item xs={4} sm={2} className={classes.removeIconContainer}>
          <Box>
            <Tooltip title={removeFileName}>
              <span>
                <PGButton
                  variant={'outlined'}
                  onClick={() => removeDocument(document)}
                  startIcon={<RemoveCircleOutlineIcon />}
                  label={t('common_remove')}
                />
              </span>
            </Tooltip>
          </Box>
        </Grid>
      )}
      {!hideDivider && <Divider sx={{my: 3}} />}
    </>
  )
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    background: theme.palette.common.white,
  },
  fileIcon: {
    color: theme.palette.primary.main,
  },
  validIcon: {
    color: '#B6BAC8',
  },
  fileName: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeIconContainer: {
    button: {
      borderRadius: 3,
      cursor: 'pointer',
    },
  },
}))

export default RemoteFilePreview
